import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Receipt, Shield, ShieldAlert, MessageCircle, X, ArrowRight, ArrowLeft, Zap, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const getWalkthroughSteps = (userCredits) => [
  {
    id: 'welcome',
    title: 'Welcome to SettledInFull',
    description: 'Let us show you around. This quick tour highlights the key tools to help you get paid faster.',
    icon: null,
    visual: (
      <div className="flex items-center justify-center gap-3 my-6">
        <div className="w-14 h-14 rounded-xl bg-[#FF4500] flex items-center justify-center p-2.5">
          <img src="/handshake-white.png" alt="SettledInFull" className="w-full h-full object-contain" />
        </div>
      </div>
    ),
    navTo: null,
  },
  {
    id: 'credits',
    title: 'Your Credit Balance',
    description: 'Everything in SettledInFull runs on credits. You start with 2 free credits — enough to try the tools right away.',
    icon: Zap,
    iconColor: 'text-[#FF4500]',
    iconBg: 'bg-[#FF4500]/10',
    navTo: null,
    extra: (
      <div className="mt-4 space-y-3">
        <div className="p-4 rounded-xl bg-gradient-to-br from-[#FF4500]/10 to-transparent border border-[#FF4500]/20 text-center">
          <p className="text-3xl font-bold text-[#FF4500]" data-testid="walkthrough-credit-balance">{userCredits ?? 0}</p>
          <p className="text-white/50 text-xs mt-1">Credits Available</p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="p-2 rounded-lg bg-white/5 border border-white/6">
            <p className="text-sm font-semibold text-white">1 credit</p>
            <p className="text-[10px] text-white/40">Reply / Invoice / Download</p>
          </div>
          <div className="p-2 rounded-lg bg-white/5 border border-white/6">
            <p className="text-sm font-semibold text-white">3 credits</p>
            <p className="text-[10px] text-white/40">Full email set (3 tones)</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'topup',
    title: 'Top Up Anytime',
    description: 'Need more credits? Hit the glowing "Top Up Credits" button in the navigation bar — it\'s always visible when you\'re logged in. Choose from fixed packs or enter a custom amount.',
    icon: CreditCard,
    iconColor: 'text-[#10B981]',
    iconBg: 'bg-[#10B981]/10',
    navTo: null,
    extra: (
      <div className="mt-3 p-3 rounded-lg bg-black/30 border border-white/6 flex items-start gap-3">
        <Zap className="w-5 h-5 text-[#FF4500] shrink-0 mt-0.5" />
        <div>
          <p className="text-white/80 text-sm font-medium">Look for the glow</p>
          <p className="text-white/40 text-xs mt-0.5">The orange "Top Up Credits" button pulses gently in your navbar so you never lose track.</p>
        </div>
      </div>
    ),
  },
  {
    id: 'email-generator',
    title: 'Email Generator',
    description: 'Generate professional payment collection emails in three tones — Friendly, Firm, and Final Demand. Download as PDF or copy to clipboard.',
    icon: FileText,
    iconColor: 'text-[#FF4500]',
    iconBg: 'bg-[#FF4500]/10',
    screenshot: 'https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/dknwwvge_pexels-polina-zimmerman-3747075.jpg',
    screenshotAlt: 'Email Generator - Create professional payment collection emails',
    navTo: '/email-generator',
    ctaLabel: 'Go to Email Generator',
  },
  {
    id: 'invoice-creator',
    title: 'Invoice Creator',
    description: 'Create professional invoices from 10 unique templates. Add your logo, line items, payment terms, and download clean PDFs.',
    icon: Receipt,
    iconColor: 'text-[#10B981]',
    iconBg: 'bg-[#10B981]/10',
    screenshot: 'https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/vjo2oo2e_pexels-kindelmedia-7688123.jpg',
    screenshotAlt: 'Invoice Creator - Build and send professional invoices',
    navTo: '/invoice-creator',
    ctaLabel: 'Go to Invoice Creator',
  },
  {
    id: 'what-should-i-say',
    title: 'What Should I Say?',
    description: 'Paste an awkward client message and get 5 AI-generated responses — from Friendly to Savage. Choose the tone that fits your situation and reply with confidence.',
    icon: MessageCircle,
    iconColor: 'text-[#8B5CF6]',
    iconBg: 'bg-[#8B5CF6]/10',
    screenshot: 'https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/27p62r2w_pexels-vladislavsmigelski-12355752.jpg',
    screenshotAlt: 'What Should I Say - AI response generator for client messages',
    navTo: '/what-should-i-say',
    ctaLabel: 'Go to What Should I Say?',
  },
  {
    id: 'protection-hub',
    title: 'Payment Protection Hub',
    description: 'Access educational resources on avoiding late payments, deposit strategies, contract templates, and the Client Risk Checker to assess non-payment risk before taking on a client.',
    icon: Shield,
    iconColor: 'text-[#F59E0B]',
    iconBg: 'bg-[#F59E0B]/10',
    screenshot: 'https://images.unsplash.com/photo-1611418636162-eb07ae0aa4a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxzaGllbGQlMjBzZWN1cml0eSUyMHByb3RlY3Rpb24lMjBkb2N1bWVudHxlbnwwfHx8fDE3NzU1MDIyMDd8MA&ixlib=rb-4.1.0&q=85',
    screenshotAlt: 'Protection Hub - Payment protection resources and risk checker',
    navTo: '/protection-hub',
    ctaLabel: 'Go to Protection Hub',
    extra: (
      <div className="mt-3 p-3 rounded-lg bg-black/30 border border-white/6 flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-white/80 text-sm font-medium">Client Risk Checker</p>
          <p className="text-white/40 text-xs mt-0.5">Score potential clients based on warning behaviours to decide how to protect yourself.</p>
        </div>
      </div>
    ),
  },
];

export default function WalkthroughDemo({ onComplete }) {
  const navigate = useNavigate();
  const { user, planInfo } = useAuth();
  const [step, setStep] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const steps = getWalkthroughSteps(planInfo?.credits ?? user?.credits ?? 0);
  const current = steps[step];
  const isLast = step === steps.length - 1;
  const isFirst = step === 0;

  const handleComplete = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      localStorage.setItem('settledinfull_walkthrough_seen', 'true');
      onComplete();
    }, 300);
  }, [onComplete]);

  const handleNext = () => {
    if (isLast) {
      handleComplete();
    } else {
      setStep((s) => s + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) setStep((s) => s - 1);
  };

  const handleNavigate = (path) => {
    handleComplete();
    setTimeout(() => navigate(path), 100);
  };

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') handleComplete();
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line
  }, [step]);

  const StepIcon = current.icon;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-300 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
      data-testid="walkthrough-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleComplete} />

      {/* Card */}
      <div
        className="relative w-full max-w-lg mx-4 bg-[#0c0c12] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        data-testid="walkthrough-card"
      >
        {/* Skip button */}
        <button
          onClick={handleComplete}
          className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors z-10"
          data-testid="walkthrough-skip-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pt-6">
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? 'w-8 bg-[#FF4500]' : 'w-1.5 bg-white/15 hover:bg-white/25'
              }`}
              data-testid={`walkthrough-dot-${i}`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="px-8 pt-6 pb-4 text-center">
          {/* Icon or visual */}
          {current.visual ? (
            current.visual
          ) : StepIcon ? (
            <div className={`w-16 h-16 rounded-xl ${current.iconBg} flex items-center justify-center mx-auto mt-4 mb-4`}>
              <StepIcon className={`w-8 h-8 ${current.iconColor}`} />
            </div>
          ) : null}

          <h2 className="font-clash text-2xl font-semibold text-white mb-3" data-testid="walkthrough-title">
            {current.title}
          </h2>
          <p className="text-white/55 text-sm leading-relaxed max-w-sm mx-auto">
            {current.description}
          </p>

          {/* Screenshot preview */}
          {current.screenshot && (
            <div className="mt-4 mx-auto max-w-sm overflow-hidden rounded-lg border border-white/8">
              <img
                src={current.screenshot}
                alt={current.screenshotAlt}
                className="w-full h-36 object-cover opacity-70"
                data-testid={`walkthrough-screenshot-${current.id}`}
              />
            </div>
          )}

          {current.extra}

          {/* Navigate CTA */}
          {current.navTo && (
            <button
              onClick={() => handleNavigate(current.navTo)}
              className="mt-5 inline-flex items-center gap-2 text-[#FF4500] hover:text-[#ff5e1a] text-sm font-medium transition-colors"
              data-testid={`walkthrough-nav-${current.id}`}
            >
              {current.ctaLabel}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Footer navigation */}
        <div className="px-8 pb-6 pt-2 flex items-center justify-between">
          <div>
            {!isFirst ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrev}
                className="text-white/40 hover:text-white/70"
                data-testid="walkthrough-prev-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleComplete}
                className="text-white/30 hover:text-white/50"
                data-testid="walkthrough-skip-text-btn"
              >
                Skip tour
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-white/20">
              {step + 1} / {steps.length}
            </span>
            <Button
              onClick={handleNext}
              size="sm"
              className="bg-[#FF4500] hover:bg-[#e03e00] text-white px-5"
              data-testid="walkthrough-next-btn"
            >
              {isLast ? 'Get Started' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
